import React, { useState } from 'react';
import {
  UserPlus,
  EnvelopeSimple,
  ShieldCheck,
  CheckCircle,
  X,
  MagnifyingGlass,
  Trash,
  CircleNotch,
  WarningCircle,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAvatarForUser } from '@/utils/avatar';
import { apiClient, UserRole, UserStatus, useNotificationStore, useAuthStore } from '@dukene/shared';
import { formatDistanceToNow } from 'date-fns';
import { confirmAction } from '../../components/modals/ConfirmModal';

interface StoreManagerItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  emailVerified: boolean;
  verificationToken: string | null;
  verificationTokenExpires: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export default function StoreManagersPage() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const { addNotification } = useNotificationStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'STAFF' as UserRole,
  });

  const { data: managers, isLoading, error } = useQuery<StoreManagerItem[]>({
    queryKey: ['merchant-staff', activeStoreId],
    queryFn: async () => {
      const response = await apiClient.get<StoreManagerItem[]>('/stores/merchant/staff');
      return response;
    },
    enabled: !!activeStoreId,
  });

  const inviteMutation = useMutation({
    mutationFn: async (data: typeof inviteData) => {
      return apiClient.post('/stores/merchant/staff', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-staff', activeStoreId] });
      setIsInviteModalOpen(false);
      setInviteData({ email: '', firstName: '', lastName: '', role: 'STAFF' as UserRole });
      addNotification({
        type: 'success',
        message: 'Teammate invitation sent successfully',
      });
    },
    onError: (err: any) => {
      addNotification({
        type: 'error',
        message: err.message || 'Failed to invite staff member',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async ({ staffId }: { staffId: string; isInvite?: boolean }) => {
      return apiClient.delete(`/stores/merchant/staff/${staffId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-staff', activeStoreId] });
      addNotification({
        type: 'success',
        message: variables?.isInvite ? 'Invitation revoked successfully' : 'Manager removed successfully',
      });
    },
    onError: (err: any) => {
      addNotification({
        type: 'error',
        message: err.message || 'Failed to remove staff member',
      });
    },
  });

  const filteredManagers = (managers || []).filter((m) =>
    `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingInvites = filteredManagers.filter((manager) => !manager.emailVerified || !!manager.verificationToken);
  const activeManagers = filteredManagers.filter((manager) => manager.emailVerified && !manager.verificationToken);

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate(inviteData);
  };

  if (error) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl border border-red-200 text-red-900 max-w-4xl">
        <WarningCircle weight="fill" className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <h3 className="text-sm font-bold">Failed to load users and permissions</h3>
        <p className="text-xs text-red-700 mt-1">Please refresh the page or verify your merchant access level.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] pb-20 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <h1 className="text-xl font-bold text-[#202223] dark:text-white tracking-tight">Users and permissions</h1>
        </div>
        <button
          type="button"
          onClick={() => setIsInviteModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#202223] dark:bg-white text-white dark:text-slate-900 font-bold text-xs rounded-lg shadow-2xs hover:opacity-90 transition-all self-start sm:self-auto"
        >
          <UserPlus weight="bold" className="w-3.5 h-3.5" />
          <span>Add staff</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass className="w-3.5 h-3.5 text-[#5C5F62] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Filter staff by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-8 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
          />
        </div>
      </div>

      {/* Active Staff Members Section */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl overflow-hidden shadow-2xs">
        <div className="px-5 py-3 border-b border-[#E1E3E5] dark:border-slate-800 flex items-center justify-between bg-[#F6F6F7]/60 dark:bg-slate-800/50">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
            Active staff members ({activeManagers.length})
          </h2>
        </div>

        {isLoading ? (
          <div className="p-16 flex flex-col items-center justify-center space-y-3">
            <CircleNotch weight="bold" className="w-6 h-6 text-[#5C5F62] animate-spin" />
            <p className="text-xs text-[#5C5F62]">Loading store managers...</p>
          </div>
        ) : activeManagers.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#5C5F62] dark:text-slate-400">
            No active staff found matching your filter.
          </div>
        ) : (
          <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800">
            {activeManagers.map((manager) => (
              <div
                key={manager.id}
                className="p-4 sm:px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-emerald/10 border border-[#D2D5D9] flex shrink-0">
                    <img src={getAvatarForUser(manager.id || manager.email)} alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-[#202223] dark:text-white">
                        {manager.firstName} {manager.lastName}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/60">
                        <CheckCircle weight="fill" className="w-3 h-3 text-emerald-600" />
                        Active
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                      <EnvelopeSimple weight="fill" className="w-3.5 h-3.5" />
                      <span>{manager.email}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-6 text-xs">
                  <div className="flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-300 font-medium">
                    <ShieldCheck weight="fill" className="w-3.5 h-3.5 text-[#5C5F62]" />
                    <span>{manager.role}</span>
                  </div>

                  <span className="text-[#5C5F62] dark:text-slate-400">
                    Joined {formatDistanceToNow(new Date(manager.createdAt))} ago
                  </span>

                  <button
                    type="button"
                    onClick={async () => {
                      const confirmed = await confirmAction({
                        title: 'Remove Staff Access',
                        message: 'Remove access for this staff member?',
                        confirmText: 'Remove',
                        cancelText: 'Cancel',
                      });
                      if (confirmed) {
                        removeMutation.mutate({ staffId: manager.id });
                      }
                    }}
                    className="p-1.5 text-[#5C5F62] hover:text-red-600 rounded transition-colors"
                    title="Remove access"
                  >
                    <Trash weight="fill" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations Section */}
      {pendingInvites.length > 0 && (
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl overflow-hidden shadow-2xs">
          <div className="px-5 py-3 border-b border-[#E1E3E5] dark:border-slate-800 flex items-center justify-between bg-[#F6F6F7]/60 dark:bg-slate-800/50">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
              Pending invitations ({pendingInvites.length})
            </h2>
          </div>

          <div className="divide-y divide-[#E1E3E5] dark:divide-slate-800">
            {pendingInvites.map((invite) => (
              <div
                key={invite.id}
                className="p-4 sm:px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-blue-100/50 dark:bg-blue-950/60 border border-blue-200 flex shrink-0 opacity-80 grayscale-[30%]">
                    <img src={getAvatarForUser(invite.id || invite.email)} alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-[#202223] dark:text-white">
                        {invite.firstName} {invite.lastName}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200/80">
                        Pending verify
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                      <EnvelopeSimple weight="fill" className="w-3.5 h-3.5" />
                      <span>{invite.email}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-6 text-xs">
                  <span className="text-[#5C5F62] dark:text-slate-400">
                    Invited {formatDistanceToNow(new Date(invite.createdAt))} ago
                  </span>

                  <button
                    type="button"
                    onClick={async () => {
                      const confirmed = await confirmAction({
                        title: 'Revoke Invitation',
                        message: 'Revoke invitation?',
                        confirmText: 'Revoke',
                        cancelText: 'Cancel',
                      });
                      if (confirmed) {
                        removeMutation.mutate({ staffId: invite.id, isInvite: true });
                      }
                    }}
                    className="p-1.5 text-[#5C5F62] hover:text-red-600 rounded transition-colors"
                    title="Revoke invitation"
                  >
                    <Trash weight="fill" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-[#1A1D21] w-full max-w-[460px] rounded-xl border border-[#D2D5D9] dark:border-slate-800 shadow-xl overflow-hidden animate-in zoom-in-95 duration-150">
            <form onSubmit={handleInvite} className="p-5 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-[#E1E3E5] dark:border-slate-800">
                <h3 className="text-sm font-bold text-[#202223] dark:text-white">Add staff member</h3>
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="p-1.5 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 rounded-lg text-[#5C5F62] transition-colors"
                >
                  <X weight="bold" className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      First name
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Jane"
                      value={inviteData.firstName}
                      onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })}
                      className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      Last name
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Doe"
                      value={inviteData.lastName}
                      onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })}
                      className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Email address
                  </label>
                  <input
                    required
                    type="email"
                    placeholder="teammate@example.com"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="invite-role-select" className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Assigned role
                  </label>
                  <select
                    id="invite-role-select"
                    value={inviteData.role}
                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as UserRole })}
                    className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white focus:border-[#202223] focus:outline-none"
                  >
                    <option value="STAFF">Staff (Standard store access)</option>
                    <option value="MERCHANT">Admin (Full store permissions)</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E1E3E5] dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-3.5 py-1.5 text-xs font-semibold text-[#5C5F62] hover:text-[#202223] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 shadow-2xs"
                >
                  {inviteMutation.isPending && <CircleNotch weight="bold" className="w-3.5 h-3.5 animate-spin" />}
                  Send invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
